import React from "react";

import zoomSdk from "@zoom/appssdk";
import { isString } from "lodash/fp";
import {
  Link as RouteLink,
  LinkProps as RouteLinkProps,
} from "react-router-dom";

import MuiLink, { LinkProps as MuiLinkProps } from "@mui/material/Link";
import styled from "@mui/material/styles/styled";

import useApps from "@app/common/hooks/useApps";
import Config from "@app/config";
import { configureZoomSdk } from "@app/embedded/utils";
import openAppBrowser from "@app/embedded/zoom/utils/openAppBrowser";
import logger from "@app/utils/logger";
import { getAppUrl } from "@app/utils/utils";

const NEW_TAB_PROPS = { rel: "noopener noreferrer", target: "_blank" };

export interface LinkProps
  extends Omit<MuiLinkProps, "underline" | "fontWeight"> {
  /** Will force the link to open in a new tab. Unnecessary to pass when also passing the `href` prop */
  newTab?: boolean;
  /** Path to open. Will open a new tab by default */
  href?: string;
  replace?: RouteLinkProps["replace"];
  state?: RouteLinkProps["state"];
  to?: RouteLinkProps["to"];
  /** Whether the link should open in a browser only when in Zoom client. In-browser experience will be
   * dictated by other props */
  openInBrowser?: boolean;
  underline?: boolean;
  fontWeight?: number;
}

const getExternalToFromProps = (
  to?: LinkProps["to"],
  href?: LinkProps["href"]
): { path?: string; search?: string } => {
  if (href) return { path: href };
  if (isString(to)) return { path: to };
  return { path: to?.pathname, search: to?.search };
};

interface StyledMuiLinkProps
  extends Omit<MuiLinkProps, "underline" | "fontWeight"> {
  to?: RouteLinkProps["to"];
  fontWeight?: number;
  underline?: boolean;
}

const MuiLinkWithRef = React.forwardRef<HTMLAnchorElement, StyledMuiLinkProps>(
  ({ fontWeight: _f, underline: _u, ...props }, ref) => (
    <MuiLink {...props} ref={ref} />
  )
);

MuiLinkWithRef.displayName = "MuiLinkWithRef";

const StyledMuiLink = styled(MuiLinkWithRef)(
  ({ fontWeight, underline, onClick }) => ({
    fontWeight,
    textDecoration: underline ? "underline" : "none",
    ...(onClick && {
      "&:hover": { cursor: "pointer" },
    }),
  })
);

/**
 * Generic Link component. Should be used for cases where the link won't ever be in the Zoom client or if
 * the link is simply a navigation to some place else within our application (w/o opening a new tab)
 * */
const Link = React.forwardRef(
  (
    { children, to, href, newTab, openInBrowser: _, ...props }: LinkProps,
    ref: React.ForwardedRef<HTMLAnchorElement>
  ) => {
    // hrefs will always open a new tab
    const newTabProps = newTab || Boolean(href) ? NEW_TAB_PROPS : {};

    if (to) {
      return (
        <StyledMuiLink
          {...newTabProps}
          {...props}
          to={to}
          ref={ref}
          component={RouteLink}
        >
          {children}
        </StyledMuiLink>
      );
    }

    return (
      <StyledMuiLink {...newTabProps} {...props} ref={ref} href={href}>
        {children}
      </StyledMuiLink>
    );
  }
);

Link.displayName = "Link";

const ZoomExternalLink = React.forwardRef(
  (props: LinkProps, ref: React.ForwardedRef<HTMLAnchorElement>) => {
    const { children, href, onClick, to, ...other } = props;
    const externalTo = getExternalToFromProps(to, href);

    if (externalTo.path && externalTo.path.includes("mailto")) {
      // opening up a mailto doesn't work in Zoom, so just turn into plaintext
      return <>{children}</>;
    }

    const url = externalTo.path ? getAppUrl(externalTo.path) : undefined;

    const handleClick: LinkProps["onClick"] = async (
      event: React.MouseEvent<HTMLAnchorElement>
    ) => {
      // stop propagation so that the link doesn't try to redirect within the app (a new
      // browser window is going to open instead)
      event.stopPropagation();

      onClick?.(event);

      if (!url) return;

      const routeRequiresAuthentication = url.includes(Config.FRONTEND_URL);

      logger.debug("ZoomExternalLink configureZoomSdk");
      await configureZoomSdk();
      return routeRequiresAuthentication
        ? openAppBrowser(externalTo.path!, externalTo.search)
        : zoomSdk.openUrl({ url });
    };

    return (
      <Link {...other} href={url} ref={ref} onClick={handleClick}>
        {children}
      </Link>
    );
  }
);

ZoomExternalLink.displayName = "ZoomExternalLink";

/**
 * Link component that is knowledgeable of the current client and will open new tabs/hrefs
 * in the browser if in the Zoom client.
 * */
const ClientCompatibleLink = React.forwardRef(
  (props: LinkProps, ref: React.ForwardedRef<HTMLAnchorElement>) => {
    const { href, openInBrowser, newTab } = props;
    const { client } = useApps();

    const requiresZoomLink =
      client === "zoom" && (openInBrowser || newTab || Boolean(href));

    if (requiresZoomLink) {
      return <ZoomExternalLink ref={ref} {...props} />;
    }

    return <Link ref={ref} {...props} />;
  }
);

ClientCompatibleLink.displayName = "ClientCompatibleLink";

export { Link };
export default ClientCompatibleLink;
